import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import Category, { CategoryType } from '@/models/category';
import { useCategories } from '@/services/categories';
import CategoryModal from './components/category-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';

// Legacy categories (no `type`) are content categories by definition.
const typeLabel = (type?: CategoryType): string => {
    switch (type) {
        case 'course':
            return 'Course';
        case 'blog':
            return 'Blog';
        default:
            return 'Content';
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    if (order === 'desc') {
        return (a: any, b: any) => {
            if (a[orderBy] < b[orderBy]) {
                return -1;
            }
            if (a[orderBy] > b[orderBy]) {
                return 1;
            }
            return 0;
        };
    }
    return (a: any, b: any) => {
        if (a[orderBy] > b[orderBy]) {
            return -1;
        }
        if (a[orderBy] < b[orderBy]) {
            return 1;
        }
        return 0;
    };
};

const Categories = () => {
    // 'all' so blog-only categories stay visible/editable in the manager (the
    // App's content-scoped /categories would hide them).
    const { data: categories, isLoading } = useCategories('all');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredCategories, setFilteredCategories] = useState<Category[]>(categories?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>();

    useEffect(() => {
        if (categories?.data) {
            setFilteredCategories(categories.data);
        }
    }, [categories]);

    const toggle = () => {
        setIsOpen((prevState) => !prevState);
    };

    const handleRequestAdd = () => {
        setSelectedCategoryId(undefined);
        toggle();
    };

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
        const orderByTemp = property;
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }

        setOrder(orderTemp);
        setOrderBy(orderByTemp);
    };

    const handleClick = (id: string) => {
        setSelectedCategoryId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => {
        setPage(item);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Categories</h3>
                    <h3 className="page-subhead subhead">These are used to categorize the contents</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Name"
                                            type="text"
                                            onChange={(e) =>
                                                setFilteredCategories(
                                                    categories?.data?.filter((c) => c.name.toLowerCase().includes(e.target.value.toLowerCase())) ||
                                                        [],
                                                )
                                            }
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filteredCategories
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((d) => {
                                                        return (
                                                            <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                                <TableCell component="th" scope="row" padding="none">
                                                                    {d.id}
                                                                </TableCell>
                                                                <TableCell>{d.name}</TableCell>
                                                                <TableCell>{typeLabel(d.type)}</TableCell>
                                                                <TableCell>{d.hidden ? 'Hidden' : 'Visible'}</TableCell>
                                                                <TableCell>{formatDate(d.created_at)}</TableCell>
                                                                <TableCell>{formatDate(d.updated_at)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                {filteredCategories.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} className="text-center">
                                                            No Data to display
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filteredCategories.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{
                                            inputProps: { 'aria-label': 'rows per page' },
                                            native: true,
                                        }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <CategoryModal isOpen={isOpen} toggle={toggle} selectedCategoryId={selectedCategoryId} />
        </Container>
    );
};

export default Categories;
